// SPDX-FileCopyrightText: 2017-2022 City of Espoo
//
// SPDX-License-Identifier: LGPL-2.1-or-later

import { useContext, useState } from 'react'
import styled from 'styled-components'

import type { ChildBackupPickup } from 'lib-common/generated/api-types/backuppickup'
import type { ChildId } from 'lib-common/generated/api-types/shared'
import { useMutationResult, useQueryResult } from 'lib-common/query'
import AddButton from 'lib-components/atoms/buttons/AddButton'
import { IconOnlyButton } from 'lib-components/atoms/buttons/IconOnlyButton'
import InputField from 'lib-components/atoms/form/InputField'
import { Table, Tbody, Td, Th, Thead, Tr } from 'lib-components/layout/Table'
import {
  FixedSpaceColumn,
  FixedSpaceRow
} from 'lib-components/layout/flex-helpers'
import FormModal from 'lib-components/molecules/modals/FormModal'
import InfoModal from 'lib-components/molecules/modals/InfoModal'
import { H3, Label } from 'lib-components/typography'
import { faPen, faQuestion, faTrash } from 'lib-icons'

import { ChildContext } from '../../state'
import { useTranslation } from '../../state/i18n'
import { UIContext } from '../../state/ui'
import { renderResult } from '../async-rendering'
import { FlexRow } from '../common/styled/containers'

import {
  createBackupPickupMutation,
  deleteBackupPickupMutation,
  getBackupPickupsQuery,
  updateBackupPickupMutation
} from './queries'

interface BackupPickupProps {
  childId: ChildId
}

function BackupPickup({ childId }: BackupPickupProps) {
  const { i18n } = useTranslation()
  const { uiMode, toggleUiMode, clearUiMode } = useContext(UIContext)
  const { permittedActions } = useContext(ChildContext)

  const backupPickups = useQueryResult(getBackupPickupsQuery({ childId }))
  const { mutateAsync: deleteBackupPickup } = useMutationResult(
    deleteBackupPickupMutation
  )

  const [backupPickup, setBackupPickup] = useState<
    ChildBackupPickup | undefined
  >(undefined)

  const openEditBackupPickupModal = (pickup: ChildBackupPickup) => {
    setBackupPickup(pickup)
    toggleUiMode(`edit-backup-pickup`)
  }

  const openRemoveBackupPickupModal = (pickup: ChildBackupPickup) => {
    setBackupPickup(pickup)
    toggleUiMode(`remove-backup-pickup`)
  }

  const closeModal = () => {
    setBackupPickup(undefined)
    clearUiMode()
  }

  const confirmRemoveModal = async () => {
    if (backupPickups.isSuccess && backupPickup) {
      await deleteBackupPickup({ childId, id: backupPickup.id })
      closeModal()
    }
  }

  return (
    <>
      {renderResult(backupPickups, ({ backupPickups }) => (
        <>
          <FlexRow $justifyContent="space-between">
            <H3 $noMargin>{i18n.childInformation.backupPickups.title}</H3>
            {permittedActions.has('CREATE_BACKUP_PICKUP') && (
              <AddButton
                text={i18n.childInformation.backupPickups.add}
                onClick={() => toggleUiMode('create-backup-pickup')}
                data-qa="create-backup-pickup-btn"
              />
            )}
          </FlexRow>
          {backupPickups.length > 0 && (
            <Table>
              <Thead>
                <Tr>
                  <Th>{i18n.childInformation.backupPickups.name}</Th>
                  <Th>{i18n.childInformation.backupPickups.phone}</Th>
                  {backupPickups.some((r) => r.permittedActions.length > 0) && (
                    <Th />
                  )}
                </Tr>
              </Thead>
              <Tbody>
                {backupPickups.map((row) => (
                  <Tr
                    key={row.backupPickup.id}
                    data-qa={`table-backup-pickup-row-${row.backupPickup.name}`}
                  >
                    <Td data-qa="backup-pickup-name">
                      {row.backupPickup.name}
                    </Td>
                    <Td>{row.backupPickup.phone}</Td>
                    {(row.permittedActions.includes('UPDATE') ||
                      row.permittedActions.includes('DELETE')) && (
                      <Td>
                        <FixedSpaceRowAlignRight>
                          {row.permittedActions.includes('UPDATE') && (
                            <IconOnlyButton
                              icon={faPen}
                              onClick={() =>
                                openEditBackupPickupModal(row.backupPickup)
                              }
                              data-qa="edit-backup-pickup"
                              aria-label={i18n.common.edit}
                            />
                          )}
                          {row.permittedActions.includes('DELETE') && (
                            <IconOnlyButton
                              icon={faTrash}
                              onClick={() =>
                                openRemoveBackupPickupModal(row.backupPickup)
                              }
                              data-qa="delete-backup-pickup"
                              aria-label={i18n.common.remove}
                            />
                          )}
                        </FixedSpaceRowAlignRight>
                      </Td>
                    )}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </>
      ))}
      {uiMode === `create-backup-pickup` && (
        <CreateBackupPickupModal childId={childId} onClose={closeModal} />
      )}
      {uiMode === `remove-backup-pickup` && (
        <InfoModal
          type="warning"
          title={i18n.childInformation.backupPickups.removeConfirmation}
          icon={faQuestion}
          reject={{ action: closeModal, label: i18n.common.cancel }}
          resolve={{ action: confirmRemoveModal, label: i18n.common.remove }}
        />
      )}
      {uiMode === `edit-backup-pickup` && backupPickup && (
        <EditBackupPickupModal
          childId={childId}
          backupPickup={backupPickup}
          onClose={closeModal}
        />
      )}
    </>
  )
}

interface CreateBackupPickupModalProps {
  childId: ChildId
  onClose: () => void
}

function CreateBackupPickupModal({
  childId,
  onClose
}: CreateBackupPickupModalProps) {
  const { i18n } = useTranslation()
  const { mutateAsync: createBackupPickup } = useMutationResult(
    createBackupPickupMutation
  )
  const [name, setName] = useState<string>('')
  const [phone, setPhone] = useState<string>('')

  async function saveBackupPickup() {
    if (name !== '' && phone !== '') {
      await createBackupPickup({ childId, body: { name, phone } })
      onClose()
    }
  }

  return (
    <FormModal
      title={i18n.childInformation.backupPickups.add}
      resolveAction={saveBackupPickup}
      resolveLabel={i18n.common.save}
      rejectAction={onClose}
      rejectLabel={i18n.common.cancel}
    >
      <FixedSpaceColumn>
        <FixedSpaceColumn $spacing="xxs">
          <Label>{i18n.childInformation.backupPickups.name}</Label>
          <InputField
            value={name}
            onChange={setName}
            placeholder={i18n.childInformation.backupPickups.name}
            width="full"
            data-qa="backup-pickup-name-input"
          />
        </FixedSpaceColumn>
        <FixedSpaceColumn $spacing="xxs">
          <Label>{i18n.childInformation.backupPickups.phone}</Label>
          <InputField
            value={phone}
            onChange={setPhone}
            placeholder={i18n.childInformation.backupPickups.phone}
            width="full"
            data-qa="backup-pickup-phone-input"
          />
        </FixedSpaceColumn>
      </FixedSpaceColumn>
    </FormModal>
  )
}

interface EditBackupPickupModalProps {
  childId: ChildId
  backupPickup: ChildBackupPickup
  onClose: () => void
}

function EditBackupPickupModal({
  childId,
  backupPickup,
  onClose
}: EditBackupPickupModalProps) {
  const { i18n } = useTranslation()
  const { mutateAsync: updateBackupPickup } = useMutationResult(
    updateBackupPickupMutation
  )
  const [name, setName] = useState<string>(backupPickup.name)
  const [phone, setPhone] = useState<string>(backupPickup.phone)

  async function saveBackupPickup() {
    await updateBackupPickup({
      childId,
      id: backupPickup.id,
      body: {
        name: name !== '' ? name : backupPickup.name,
        phone: phone !== '' ? phone : backupPickup.phone
      }
    })
    onClose()
  }

  return (
    <FormModal
      title={i18n.childInformation.backupPickups.edit}
      resolveAction={saveBackupPickup}
      resolveLabel={i18n.common.save}
      rejectAction={onClose}
      rejectLabel={i18n.common.cancel}
    >
      <FixedSpaceColumn>
        <FixedSpaceColumn $spacing="xxs">
          <Label>{i18n.childInformation.backupPickups.name}</Label>
          <InputField
            value={name}
            onChange={setName}
            placeholder={i18n.childInformation.backupPickups.name}
            width="full"
            data-qa="backup-pickup-name-input"
          />
        </FixedSpaceColumn>
        <FixedSpaceColumn $spacing="xxs">
          <Label>{i18n.childInformation.backupPickups.phone}</Label>
          <InputField
            value={phone}
            onChange={setPhone}
            placeholder={i18n.childInformation.backupPickups.phone}
            width="full"
            data-qa="backup-pickup-phone-input"
          />
        </FixedSpaceColumn>
      </FixedSpaceColumn>
    </FormModal>
  )
}

const FixedSpaceRowAlignRight = styled(FixedSpaceRow)`
  justify-content: flex-end;
`

export default BackupPickup
